import { FC, useCallback, useEffect, useState } from "react";
import ParkProfileLayout from "../../ParkProfileLayout";
import { useParams } from "react-router-dom";
import useFilters from "../../../../../hooks/useFilters";
import useFetch from "../../../../../hooks/useFetch";
import moment from "moment";
import CustomTable from "../../../../../components/table/CustomTable";
import DeletePopover from "../../../../../components/buttons/DeletePopover";
import { WindTurbineService } from "../../../../../services/windTurbine/windTurbineService";
import { ActionIcon, Button, Tooltip } from "rizzui";
import cn from "../../../../../utils/classNames";
import ParkTurbinesModal from "./ParkTurbinesModal";
import { MdOutlineModeEditOutline } from "react-icons/md";

const columnOrder = [
    'id',
    'name',
    'alias',
    'shaft',
    'diameter',
    'capacity',
    'technology',
    'coordinates',
    'createdAt',
    'updatedAt',
];

const ParkTurbines: FC<{}> = () => {

    // STATES

    const [updateList, setUpdateList] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [windTurbine, setWindTurbine] = useState<any>(null);
    const windTurbineService = new WindTurbineService();

    // HOOKS

    const { id = '' } = useParams<{ id: string }>();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters();

    // DATA FETCHING

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!filters.filter_filters?.park) return null;
        const response = await windTurbineService.listWindTurbines(filters);
        return response.getResponseData();
    }, [filters]));

    // FUNCTIONS

    // --------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL BORRADO MULTIPLE DE DOCUMENTOS
     * @EN HANDLES THE MULTIPLE DELETION OF DOCUMENTS
     * 
     * @param ids 
     */
    // --------------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        await windTurbineService.deleteMultiWindTurbine(ids);
        setUpdateList(true);
        refetch();
    };
    // --------------------------------------------------------------------------------------------------------------------------

    // --------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL ORDEN DE LAS COLUMNAS
     * @EN HANDLES THE ORDER OF THE COLUMNS
     */
    // --------------------------------------------------------------------------------------------------------------------------
    const orderedData = data?.data?.map((row: any) => {
        const formattedRow: any = {
            id: row?.id,
            name: row?.name,
            alias: row?.alias,
            shaft: row?.shaft,
            diameter: row?.diameter,
            capacity: row?.capacity,
            technology: row?.technology,
            coordinates: row?.coordinates,
            createdAt: moment(row?.createdAt?.date).format('DD/MM/YYYY'),
            updatedAt: moment(row?.updateAt?.date).format('DD/MM/YYYY'),
        };

        const orderedRow: any = {};
        columnOrder.forEach((column) => {
            orderedRow[column] = formattedRow[column];
        });

        return orderedRow;
    });
    // --------------------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERAR MODAL
     * @EN CLOSE MODAL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const onClose =  () => {
        setIsOpen(false);
        setTimeout(() => {  
            setWindTurbine(null);
        }
        , 150);
    };
    //-----------------------------------------------------------------------------------------------------------------------

    // USE EFFECTS
    
    useEffect(() => {
        updateFilters({ park: id });
    }, [id]);

    useEffect(() => {
        if (updateList) {
            refetch();
            setUpdateList(false);
        }
    }, [updateList]);

    // RENDER

    return (
        <div>
            <div className="mx-auto w-100 max-h-[600px] overflow-auto">
                <div className="grid grid-cols-1 gap-4">
                    <div className="md:col-span-3 col-span-1">
                        <div className="grid gap-7">
                            <CustomTable
                                id={'park-wind-turbine-table'}
                                divClassName='max-h-table-park'
                                classNameTable='fs-table'
                                headerTableClassName='fs-table-header'
                                columnOrder={columnOrder}
                                data={orderedData} 
                                addicionalButtons={
                                    <div className="flex items-center gap-2">
                                        <Button
                                            color="primary"
                                            onClick={() => {  setIsOpen(true); }}
                                            className={cn(['mt-4 md:mt-0'])}
                                        >
                                            Crear Aerogenerador
                                        </Button>
                                    </div>  
                                }
                                isLoading={loading}
                                overrideColumns={[
                                    {
                                        key: 'shaft',
                                        customHeader: 'Fuste',
                                        render: (row: any) => {
                                            return (
                                                <>
                                                    <p key="shaft">{row.row.original.shaft ? row.row.original.shaft + " (m)" : '-'} </p>
                                                </>
                                            );
                                        }
                                    },
                                    {
                                        key: 'diameter',
                                        customHeader: 'Diametro',
                                        render: (row: any) => {
                                            return (
                                                <>
                                                    <p key="diameter">{row.row.original.diameter ? row.row.original.diameter + " (m)" : '-'}</p>
                                                </>
                                            );
                                        }
                                    },
                                    {
                                        key: 'capacity',
                                        customHeader: 'Capacidad',
                                        render: (row: any) => {
                                            return (
                                                <>
                                                    <p key="capacity">{row.row.original.capacity ? row.row.original.capacity + " (MW)" : '-'}</p>
                                                </>
                                            );
                                        }
                                    },
                                    {
                                        key: 'technology',
                                        customHeader: 'Tecnologia',
                                        render: (row: any) => {
                                            return (
                                                <>
                                                    <p key="technology">{row.row.original.technology || 'No definido'}</p>
                                                </>
                                            );
                                        }
                                    },
                                    {
                                        key: 'coordinates',
                                        customHeader: 'Coordenadas',
                                        render: (row: any) => {
                                            const [ h,x,y ] = row.row.original?.coordinates?.split(',') || [];

                                            if (!h || !x || !y) return <p key="coordinates">No definidas</p>

                                            return (
                                                <>
                                                    <p key="h">H: {h}S</p>
                                                    <p key="x">X: {x}</p>
                                                    <p key="y">Y: {y}</p>
                                                </>
                                            );
                                        }
                                    },
                                ]}
                                actions={[
                                    {
                                        label: 'Edit',
                                        permissions: { group: 'park', permission: 'edit' },
                                        render: (row: any) => {
                                            return (
                                                <Tooltip
                                                    size="sm"
                                                    content={'Editar parque'}
                                                    placement="top"
                                                    color="invert"
                                                >
                                                    <ActionIcon
                                                        as="span"
                                                        size="sm"
                                                        variant="outline"
                                                        className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer h-5 w-5"
                                                        onClick={() => {
                                                            setIsOpen(true);
                                                            setWindTurbine(row);
                                                        }}
                                                    >
                                                        <MdOutlineModeEditOutline className="h-3 w-3" size={20} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            );
                                        },
                                    },
                                    {
                                        label: 'Delete',
                                        render: (row: any) => {
                                            return (
                                                <DeletePopover
                                                    actionIconClassName="w-5 h-5"
                                                    title={`Eliminar Aerogenerador`}
                                                    description={`¿Estás seguro de que deseas eliminar el aerogenerador?`}
                                                    onDelete={() => handleMultiDelete([row.id])}
                                                />
                                            );
                                        },
                                    },
                                ]}
                                handleMultipleDelete={handleMultiDelete}
                                filters={filters}
                                updateFilters={updateFilters}
                                updateFilterOrder={updateFilterOrder}
                                defaultOrder={filters.filter_order || undefined}
                                paginationData={{
                                    pageSize: filters.limit,
                                    currentPage: filters.page,
                                    pageCount: data ? data.lastPage : 1,
                                    totalCount: data?.totalRegisters,
                                    handlePagination: updatePage,
                                    handlePerPage: updatePageSize,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ParkTurbinesModal isOpen={isOpen} refetch={refetch} onClose={onClose} windTurbine={windTurbine} parkId={id} />
        </div>
    );
}

export default ParkTurbines;